import { useEffect, useState } from "react"
import type { DragEndEvent, DragStartEvent, DragOverEvent } from "@dnd-kit/core"
import { DndContext, DragOverlay, pointerWithin } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import { default as Card } from "./activities-weekly-card"
import { default as Column } from "./activities-weekly-column"
import { default as ColumnRescheduled } from "./activities-weekly-column-rescheduled"
import type { Activity, Activities } from "../../../entities/activities"
import { useActivitiesStore } from "../../../stores/use-activities-store"
import { isCurrentDay } from "../../../utils/time"
import { convertToSeconds } from "@/shared/utils/time"
import useUpdateActivity from "@/modules/study/queries/use-update-activity"
import { differenceInSeconds, isBefore, isSameDay, isToday } from "date-fns"
import type { UserEvent } from "@/services/api/cronos/user/events/get-all"
import { useActivitiesFilter } from "@/modules/study/stores/use-activities-filter-store"
import { overTimeInSeconds } from "@/modules/study/constants/calendar-overtime"

interface ActivitiesWeeklyBoardProps {
  setIsDragging: (dragging: boolean) => void
  isDragging: boolean
  events: UserEvent[]
}

const getTotalSecondsOfEvents = (events: UserEvent[]): number => {
  const totalSeconds = events.reduce((total, event) => {
    const startDate = new Date(event.startDate)
    const endDate = new Date(event.endDate)
    const eventDurationInSeconds = differenceInSeconds(endDate, startDate)
    return total + eventDurationInSeconds
  }, 0)

  return totalSeconds
}

export default function ActivitiesWeeklyBoard({
  setIsDragging,
  isDragging,
  events,
}: ActivitiesWeeklyBoardProps) {
  const updateActivity = useUpdateActivity()
  const { activities, setActivities } = useActivitiesStore()
  const [activityColumns, setActivityColumns] = useState<Activities[]>([])
  const [activity, setActivity] = useState<Activity | null>(null)
  const [activeColumn, setActiveColumn] = useState<string | null>(null)
  const [sourceColumn, setSourceColumn] = useState<Activities | null>(null)
  const { filteredSubjects: activeSubjects } = useActivitiesFilter()

  useEffect(() => {
    setActivityColumns(activities)
  }, [activities])

  useEffect(() => {
    const newActivityColumns = activities.map((column) => {
      const pendingActivities = column.activities.filter((activity) =>
        ["pending", "failed", "rescheduled"].includes(activity.status),
      )

      return {
        ...column,
        totalSeconds: pendingActivities.reduce((total, activity) => {
          const isActivityWithActiveSubject = activeSubjects.some(
            (filtered) => filtered.id === activity.subject.id,
          )

          return (
            total +
            (isActivityWithActiveSubject ? convertToSeconds(activity.estimatedDuration) || 0 : 0)
          )
        }, 0),
      }
    })

    setActivityColumns(newActivityColumns)
  }, [activeSubjects, activities])

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event
    const activity = activityColumns
      .flatMap((column) => column.activities)
      .find((task) => task.id === active.id)

    if (activity) {
      setActivity(activity)
      const sourceColumn = activityColumns.find((col) =>
        col.activities.some((t) => t.id === active.id),
      )
      if (sourceColumn) {
        setActiveColumn(sourceColumn.id)
        setSourceColumn(sourceColumn)
      }
    }

    setIsDragging(true)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event
    if (!over) return

    const overId = over.id.toString()

    // find the target column...
    const targetColumnId = overId.includes("column-")
      ? overId.replace("column-", "")
      : activityColumns.find((col) => col.activities.some((activity) => activity.id === overId))?.id

    if (targetColumnId) {
      setActiveColumn(targetColumnId)
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || !activeColumn) {
      setActivity(null)
      setActiveColumn(null)
      return
    }

    const overId = over.id.toString()

    // find the target column
    const destinationColumnId = overId.includes("column-")
      ? overId.replace("column-", "")
      : activityColumns.find((col) => col.activities.some((activity) => activity.id === overId))
          ?.id || activeColumn

    const sourceColumnIndex = activityColumns.findIndex((col) =>
      col.activities.some((activity) => activity.id === active.id),
    )
    const destinationColumnIndex = activityColumns.findIndex(
      (col) => col.id === destinationColumnId,
    )

    if (sourceColumnIndex === -1 || destinationColumnIndex === -1) {
      setActivity(null)
      setActiveColumn(null)
      setIsDragging(false)
      return
    }

    const destinationColumnDate = new Date(activityColumns[destinationColumnIndex].date)
    const isAnOldColumn =
      isBefore(destinationColumnDate, new Date()) && !isToday(destinationColumnDate)

    const isTheSameColumn = sourceColumnIndex === destinationColumnIndex

    if (isAnOldColumn || isTheSameColumn) {
      setActivity(null)
      setActiveColumn(null)
      setIsDragging(false)
      return
    }

    const newColumns = [...activityColumns]
    const sourceColumn = newColumns[sourceColumnIndex]
    const destinationColumn = newColumns[destinationColumnIndex]

    if (sourceColumnIndex === destinationColumnIndex) {
      // reorder within the same column
      const activityIndex = sourceColumn.activities.findIndex((act) => act.id === active.id)
      const overIndex = sourceColumn.activities.findIndex((act) => act.id === over.id)

      if (activityIndex !== -1 && overIndex !== -1) {
        sourceColumn.activities = arrayMove(sourceColumn.activities, activityIndex, overIndex)
      }
    } else {
      // move between different columns
      const activityIndex = sourceColumn.activities.findIndex((act) => act.id === active.id)
      if (activityIndex === -1) {
        setActivity(null)
        setActiveColumn(null)
        setIsDragging(false)
        return
      }

      const [activity] = sourceColumn.activities.splice(activityIndex, 1)
      const overTaskIndex = destinationColumn.activities.findIndex((t) => t.id === over.id)

      // calc total duration of week in seconds
      const estimatedDuration = convertToSeconds(activity.estimatedDuration)
      sourceColumn.totalSeconds = sourceColumn.totalSeconds - estimatedDuration
      destinationColumn.totalSeconds = destinationColumn.totalSeconds + estimatedDuration

      if (overTaskIndex !== -1) {
        destinationColumn.activities.splice(overTaskIndex, 0, activity)
      } else {
        destinationColumn.activities.push(activity)
      }
    }

    try {
      setIsDragging(false)
      if (destinationColumn.isRescheduled) {
        await updateActivity.mutateAsync({
          id: activity?.id ?? "",
          status: "rescheduled",
        })
      } else {
        await updateActivity.mutateAsync({
          id: activity?.id ?? "",
          dateTime: destinationColumn?.date,
          status: activity?.status === "rescheduled" ? "pending" : activity?.status,
        })
      }

      setActivities(newColumns)
      setActivityColumns(newColumns)
      setActivity(null)
      setActiveColumn(null)
    } catch (err) {
      console.error(err)
      setActivity(null)
      setActiveColumn(null)
      setIsDragging(false)
    }
  }

  return (
    <DndContext
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex h-full w-full">
        <div
          className="flex h-full w-full flex-col gap-4 pr-4 md:w-fit md:flex-row"
          style={{ contentVisibility: "auto" }}
        >
          {activityColumns.map((column) => {
            // hidde sunday
            if (column.day === "domingo") return null

            const sameDayEvents = events.filter((event) =>
              isSameDay(new Date(event.startDate), new Date(column.date)),
            )

            const isOverloaded =
              column.totalSeconds - overTimeInSeconds > getTotalSecondsOfEvents(sameDayEvents)

            return column.isRescheduled ? (
              <ColumnRescheduled
                key={column.id}
                column={column}
                isActive={column.id.toString() === activeColumn}
                isDragging={isDragging}
                isPending={updateActivity.isPending}
                sourceColumn={sourceColumn}
              />
            ) : (
              <Column
                key={column.id}
                day={column.day}
                date={column.date}
                column={column}
                totalSeconds={column.totalSeconds}
                isRescheduled={column.isRescheduled}
                isActive={column.id.toString() === activeColumn}
                isCurrentDay={isCurrentDay(column.date)}
                isDragging={isDragging}
                isPending={updateActivity.isPending}
                sourceColumn={sourceColumn}
                isOverloaded={isOverloaded}
              />
            )
          })}
        </div>
      </div>
      <DragOverlay style={{ willChange: "transform" }}>
        {activity ? (
          <div className="scale-105 transform opacity-90 shadow-xl">
            <Card {...activity} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
